/**
 * Per-booking-type behavior for the member booking detail page (S3).
 *
 * ONE details route (`/bookings/:id`) serves every booking type; what varies
 * by type lives here as data rather than conditionals scattered through the
 * JSX — the seam lesson bookings will extend rather than a second page.
 *
 * `standard` is today's behavior verbatim. `block` aliases it until its stream
 * defines its own (a member can't reach a block's detail page anyway —
 * ownership guards it out); `lesson` now carries its own cancel copy.
 *
 * @module pages/booking/bookingDetailTypeConfig
 */

import type { BookingType } from '@lgc/database/types';
import { formatPaymentTypeLabel } from '@butter/ui/domains/booking';

export interface BookingDetailTypeConfig {
  /** Which cancel endpoint the page fires — 'demo' uses the demo-cancel op (no money machinery). */
  cancelKind: 'standard' | 'demo';
  /**
   * The payment row when nothing was paid directly (`paymentType !== 'direct'`).
   * A demo says so plainly; a covered booking credits its subscription.
   */
  coverageRow: { label: string; value: string };
  /**
   * Whether the confirm modal predicts a credit/usage-reversal outcome. A demo
   * held no money and consumed no allowance — there is nothing to predict.
   */
  predictsCancelOutcome: boolean;
  /** Success toast title after a cancel. */
  cancelledToastTitle: string;
  /**
   * Cancel-confirm modal title — this page renders it directly, and the two
   * configs stay key-for-key so the lesson stream lands identically on both.
   */
  cancelSheetTitle: string;
  /**
   * Whether a receipt Total row is meaningful (a demo's is always $0.00).
   * UNWIRED here — this page's Total row is already `paymentType`-gated —
   * wired on mobile; kept for key-for-key parity.
   */
  showsTotal: boolean;
}

const standard: BookingDetailTypeConfig = {
  cancelKind: 'standard',
  coverageRow: { label: 'Membership Coverage', value: 'Included - no payment required' },
  predictsCancelOutcome: true,
  cancelledToastTitle: 'Booking cancelled',
  cancelSheetTitle: 'Are you sure you want to cancel your Booking?',
  showsTotal: true,
};

/**
 * The payment row for a booking the club comped (`paymentType: 'comp'`). Keyed on the
 * payment type, not the booking type: any standard booking can be comped, and a comp is
 * not "membership coverage" — the member may hold no plan at all.
 */
export const COMPLIMENTARY_COVERAGE_ROW: BookingDetailTypeConfig['coverageRow'] = {
  label: formatPaymentTypeLabel('comp'),
  value: 'Courtesy of the club - no payment required',
};

export const BOOKING_DETAIL_TYPE_CONFIG: Record<BookingType, BookingDetailTypeConfig> = {
  standard,
  demo: {
    cancelKind: 'demo',
    coverageRow: { label: 'Demo Session', value: 'Free - no payment required' },
    predictsCancelOutcome: false,
    cancelledToastTitle: 'Demo cancelled',
    cancelSheetTitle: 'Are you sure you want to cancel your Demo?',
    showsTotal: false,
  },
  // Cancel copy is lesson-specific. The payment rows vary per booking, not per
  // type, so they come from `lessonCoverage` rather than `coverageRow`.
  lesson: {
    ...standard,
    cancelledToastTitle: 'Lesson cancelled',
    cancelSheetTitle: 'Are you sure you want to cancel your Lesson?',
  },
  block: standard,
};
